import PropTypes from "prop-types";
import { Navigation } from "swiper/modules";
import { SwiperSlide } from "swiper/react";
import { Container, Typography } from "@mui/material";
import { Helmet } from "react-helmet";

import { useScreen, useTrackAccountEvents, useUsers } from "hooks";
import "swiper/css";
import "swiper/css/navigation";
import StarringYouPresenter from "presenters/StarringYouPresenter";
import { TEMPLATE_GROUP_TYPE_THUMB_ASPECT_RATIO } from "enums";
import withAlgoliaTracking from "components/withAlgoliaTracking";

import LoadingSkeleton from "./components/LoadingSkeleton";
import StyledSwiper from "./components/Styled/StyledSwiper";
import SlideContent from "./components/SlideContent";

const DESKTOP_HEIGHT = 321;
const MOBILE_HEIGHT = 168;
const SlideContentWithAlgoliaTracking = withAlgoliaTracking(SlideContent);

const getWidths = (templateGroupType) => {
  const [widthRatio, heightRatio] = TEMPLATE_GROUP_TYPE_THUMB_ASPECT_RATIO[templateGroupType].split("/");
  const desktopWidth = (DESKTOP_HEIGHT * widthRatio) / heightRatio;
  const mobileWidth = (MOBILE_HEIGHT * widthRatio) / heightRatio;

  return { desktopWidth, mobileWidth };
};

const CategoryRow = ({ items = [], loading, title, modulePosition, isTopRanking = false }) => {
  const { isMobile, isDesktop } = useScreen();
  const { trackSelectItemEvents } = useTrackAccountEvents();
  const { currentUser } = useUsers();

  if (loading) return <LoadingSkeleton />;

  if (!items.length) return null;

  return (
    <>
      {isTopRanking && (
        <Helmet>
          <link href="https://fonts.googleapis.com/css2?family=Red+Hat+Text:wght@700&display=swap" rel="stylesheet" />
        </Helmet>
      )}

      <Container>
        <Typography variant="h2" mb={1}>
          {title}
        </Typography>
        <StyledSwiper
          slidesPerGroupAuto={!isDesktop}
          data-testid="category-row-swiper"
          slidesPerView="auto"
          spaceBetween={isMobile ? 10 : 16}
          modules={[Navigation]}
          navigation={!isMobile}
        >
          {items.map((item, index) => {
            const { desktopWidth, mobileWidth } = getWidths(StarringYouPresenter.templateGroupType(item));

            return (
              <SwiperSlide key={StarringYouPresenter.id(item)}>
                <SlideContentWithAlgoliaTracking
                  data-testid="slide-component"
                  desktopWidth={desktopWidth}
                  mobileWidth={mobileWidth}
                  item={item}
                  index={index}
                  isTopRanking={isTopRanking}
                  isMobile={isMobile}
                  onClick={() =>
                    trackSelectItemEvents(
                      currentUser,
                      item,
                      modulePosition ? `${modulePosition}:${index + 1}` : undefined,
                    )
                  }
                />
              </SwiperSlide>
            );
          })}
        </StyledSwiper>
      </Container>
    </>
  );
};

CategoryRow.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape()),
  loading: PropTypes.bool,
  title: PropTypes.string,
  isTopRanking: PropTypes.bool,
  modulePosition: PropTypes.number,
};

export default CategoryRow;
